import React from 'react';
import { Modal, Empty, Row, Col, Statistic, List, Tag, Button } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';

const MaxBidDashboardModal = ({
  visible,
  onClose,
  activeAutoBids,
  lots,
  currentUser,
  allLotsTimeRemaining,
  setSelectedLot,
  formatCurrency,
  handleStopAutoBid
}) => {

  const hasActiveBids = activeAutoBids.size > 0;

  const totalMaxBudget = Array.from(activeAutoBids.values()).reduce(
    (sum, bid) => sum + bid.maxAmount,
    0
  );

  const totalIncrement = Array.from(activeAutoBids.values()).reduce(
    (sum, bid) => sum + bid.increment,
    0
  );

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '20px'
          }}>
            <ThunderboltOutlined />
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#262626', lineHeight: '1.2' }}>
              Max-Bid Dashboard
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>
              Manage your active max-bids
            </div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      style={{ top: 50 }}
      bodyStyle={{ padding: '24px' }}
    >
      {!hasActiveBids ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#262626', marginBottom: '16px' }}>
                ⚡ Max-Bid Dashboard
              </div>
              <div style={{ fontSize: '14px', color: '#595959', lineHeight: '1.6', textAlign: 'left', marginBottom: '12px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <strong>What is Auto-Bid?</strong>
                </div>
                <div style={{ marginBottom: '12px', paddingLeft: '8px' }}>
                  Auto-Bid (Max-Bid) automatically places bids for you when you're outbid, up to your specified max amount. Set your bid increment and max limit, and the system handles the rest.
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>How it works:</strong>
                </div>
                <ul style={{ paddingLeft: '24px', marginBottom: '12px', color: '#595959' }}>
                  <li style={{ marginBottom: '8px' }}>Set a <strong>bid increment</strong>.</li>
                  <li style={{ marginBottom: '8px' }}>Set a <strong>maximum bid amount</strong>.</li>
                  <li style={{ marginBottom: '8px' }}>The system automatically bids when outbid.</li>
                  <li style={{ marginBottom: '8px' }}>Auto-bid stops when max is reached or lot is won.</li>
                  <li>Manage multiple auto-bids simultaneously.</li>
                </ul>
                <div style={{ marginTop: '16px', padding: '12px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                  <div style={{ fontSize: '13px', color: '#1890ff', fontWeight: 600, marginBottom: '4px' }}>
                    💡 Tip:
                  </div>
                  <div style={{ fontSize: '13px', color: '#595959' }}>
                    To start an auto-bid, select any lot and use the "Show Max-Bid Options" button in lot details.
                  </div>
                </div>
              </div>
            </div>
          }
          style={{ padding: '40px 20px' }}
        />
      ) : (
        <>
          <div style={{ 
            marginBottom: 20, 
            padding: '16px 20px', 
            background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)', 
            borderRadius: '10px',
            border: '2px solid #ffd591',
            boxShadow: '0 2px 8px rgba(250, 173, 20, 0.15)'
          }}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Active Auto-Bids"
                  value={activeAutoBids.size}
                  valueStyle={{ color: '#faad14', fontSize: '24px', fontWeight: 700 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Max Budget"
                  value={formatCurrency(totalMaxBudget)}
                  valueStyle={{ color: '#ff4d4f', fontSize: '18px', fontWeight: 700 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Increment"
                  value={formatCurrency(totalIncrement)}
                  valueStyle={{ color: '#52c41a', fontSize: '18px', fontWeight: 700 }}
                />
              </Col>
            </Row>
          </div>

          <List
            dataSource={Array.from(activeAutoBids.entries())}
            renderItem={([lotId, autoBidInfo]) => {
              const lot = lots.find(l => l.id === lotId);
              const isWinning = lot?.last_bid_user_id === currentUser?.id;
              const timeRemaining = allLotsTimeRemaining[lotId] ?? lot?.time_remaining;
              const status = lot?.status;
              const isClosed = status === 'closed' || status === 'cancelled';

              return (
                <List.Item
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    background: isWinning 
                      ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                      : 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
                    borderRadius: '8px',
                    border: isWinning 
                      ? '2px solid #52c41a'
                      : '1px solid #ffd591',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <Tag color={isWinning ? 'green' : 'orange'} style={{ margin: 0, fontSize: '11px', padding: '2px 8px', fontWeight: 600 }}>
                            {isWinning ? '⚡ Winning' : '⏸ Active'}
                          </Tag>
                          <div style={{ fontSize: '16px', fontWeight: 700, color: '#262626' }}>
                            Lot #{autoBidInfo.lotNumber}: {autoBidInfo.lotName}
                          </div>
                        </div>
                        {isClosed && (
                          <Tag color="red" style={{ marginTop: '4px', fontSize: '11px' }}>
                            Lot Closed
                          </Tag>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {timeRemaining !== null && timeRemaining !== undefined && !isClosed && timeRemaining > 0 && (
                          <div style={{ fontSize: '13px', color: '#1890ff', marginBottom: '4px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>⏱️</span>
                            <span>{Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <Row gutter={16} style={{ marginBottom: '12px' }}>
                      <Col span={8}>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>Increment</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#1890ff' }}>
                          {formatCurrency(autoBidInfo.increment)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>Max Amount</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#ff4d4f' }}>
                          {formatCurrency(autoBidInfo.maxAmount)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>Current Price</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#52c41a' }}>
                          {lot ? formatCurrency(lot.current_price || lot.starting_price) : 'N/A'}
                        </div>
                      </Col>
                    </Row>

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <Button size="small" danger onClick={() => handleStopAutoBid(lotId)}>Stop</Button>
                      <Button size="small" type="primary" onClick={() => {
                        const lot = lots.find(l => l.id === lotId);
                        if (lot) {
                          setSelectedLot(lot);
                          onClose();
                        }
                      }}>View Lot</Button>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        </>
      )}
    </Modal>
  );
};

export default MaxBidDashboardModal;
